import { useEffect, useRef, useState } from "react";
import { AudioLines, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DAILY_NOTE, DailyHint, speechError, useDaily } from "@/components/MessageTools";
import { logWarn } from "@/lib/log";
import { watchSilence } from "@/lib/silence";
import { createSpeaker } from "@/lib/voiceSpeaker";

const LABEL = { listening: "Listening…", thinking: "Thinking…", speaking: "Speaking…" };

async function recordTurn(stopRef) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = ["audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  return new Promise((resolve) => {
    const stop = () => rec.state !== "inactive" && rec.stop();
    const watch = watchSilence(stream, stop);
    const poll = setInterval(() => stopRef.current && stop(), 100);
    rec.onstop = () => {
      clearInterval(poll);
      watch.close();
      stream.getTracks().forEach((t) => t.stop());
      resolve(watch.spoke ? new Blob(chunks, { type: rec.mimeType || "audio/webm" }) : null);
    };
    rec.start();
  });
}

async function transcribeTurn(blob) {
  const fd = new FormData();
  fd.append("file", blob, `turn.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
  const { data } = await http.post("/transcribe", fd, { params: { voice: true } });
  return data;
}

async function playReply(messageId, audioRef) {
  const res = await fetch(`${API}/messages/${messageId}/speech`, { method: "POST", credentials: "include" });
  if (!res.ok) throw await speechError(res);
  const audio = new Audio(URL.createObjectURL(await res.blob()));
  audioRef.current = audio;
  await new Promise((r) => { audio.onended = r; audio.onpause = r; audio.play().catch(r); });
}

const DOT = { listening: "bg-sky-400 animate-pulse", speaking: "bg-emerald-400 animate-ping", thinking: "bg-yellow-300 animate-pulse" };

function useVoiceLoop(onSend, refreshEnt) {
  const [state, setState] = useState("off");
  const stopRef = useRef(false);
  const audioRef = useRef(null);
  useEffect(() => () => { stopRef.current = true; audioRef.current?.pause(); }, []);
  const turn = async () => {
    setState("listening");
    const blob = await recordTurn(stopRef);
    if (!blob || stopRef.current) return false;
    setState("thinking");
    const data = await transcribeTurn(blob);
    refreshEnt();
    if (!data.text || stopRef.current) return false;
    const speaker = createSpeaker(data.voice_token, audioRef, stopRef, () => setState("speaking"));
    const msg = await onSend({ text: data.text, voiceToken: data.voice_token, onDelta: speaker.push });
    if (!msg?.message_id || stopRef.current) return false;
    const spoke = await speaker.finish();
    if (!spoke && !stopRef.current) { setState("speaking"); await playReply(msg.message_id, audioRef); }
    return true;
  };
  const run = async () => {
    stopRef.current = false;
    try {
      while (!stopRef.current && await turn());
    } catch (e) {
      logWarn("Voice mode stopped", e);
      if (e.response?.status !== 402) toast.error(e.name === "NotAllowedError" ? "Voice mode needs microphone access." : formatError(e));
    }
    setState("off");
  };
  const stop = () => { stopRef.current = true; audioRef.current?.pause(); setState("off"); };
  return { state, run, stop };
}

function VoiceStatus({ state }) {
  return (
    <span className="hidden sm:flex items-center gap-2 text-xs text-sky-200" data-testid="voice-state">
      <span className={`h-3 w-3 rounded-full ${DOT[state]}`} />{LABEL[state]}
    </span>
  );
}

export function VoiceMode({ onSend, disabled, trigger = 0, hideWhenIdle = false }) {
  const { refreshEnt } = useAuth();
  const { blocked } = useDaily("voice");
  const { state, run, stop } = useVoiceLoop(onSend, refreshEnt);
  const on = state !== "off";
  const toggle = () => {
    if (on) return stop();
    if (blocked) { toast.error(DAILY_NOTE); return; }
    run();
  };
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;
  useEffect(() => { if (trigger) toggleRef.current(); }, [trigger]);
  if (hideWhenIdle && !on) return null;
  return (
    <div className="flex items-center gap-2">
      {on && <VoiceStatus state={state} />}
      <button onClick={toggle} disabled={disabled && !on} data-testid="voice-mode-btn" title="Voice mode: talk hands-free"
        className={`h-9 px-3 rounded-xl flex items-center gap-1.5 text-xs transition-colors ${on ? "bg-sky-400 text-white" : "text-neutral-400 hover:text-white hover:bg-white/10"}`}>
        {state === "thinking" ? <Loader2 className="h-4 w-4 animate-spin" /> : <AudioLines className="h-4 w-4" />} <span className={on ? "" : "hidden sm:inline"}>{on ? "Stop" : "Voice"}</span>
        {!on && <span className="hidden sm:inline"><DailyHint kind="voice" testid="voice-daily-hint" /></span>}
      </button>
    </div>
  );
}
